import React, { useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import styles from './PaymentScreen.module.scss';
import { formatDate } from '../helpers/dateUtils';

import LoadingScreen from '../../../common/LoadingScreen';
import MessageBanner from '../../../common/components/MessageBanner';
import PageHeading from '../../../common/components/PageHeading';
import { PaymentInfo, PaymentInfoCTA } from '../../../common/components/PaymentInfo';
import PriceSummaryBox from '../../../common/components/PriceSummaryBox';
import SubSection from '../../../common/components/SubSection';

import EventListingsBillingContactInfo from '../components/EventListingsBillingContactInfo';

import { useFormControls } from '../../../common/hooks/useFormControls';
import useStripeValidations from '../../../common/hooks/useStripeValidations';

import { eventListingsURL } from '../constants';
import billingContactForm from '../forms/BillingContactForm';
import { scrollToTopOrError } from '../../../common/helpers/scrollTo';

const PaymentScreen = () => {
  const navigate = useNavigate();
  const { token } = useParams();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNonFieldError, setShowNonFieldError] = useState(false);
  const [billingContactInfoReadOnly, setBillingContactInfoReadOnly] = useState(false);

  const { globalState: state, initialLoad, pushEvent } = useOutletContext();
  const { eventListingPurchase: eventObj, paymentIntentClientSecret } = state ?? {};
  const { eventListingContactEmail, eventListingPriceInDollars, status } = window.eventListings;

  const {
    cardFields,
    handleCardNumberChange,
    handleCardExpiryChange,
    handleCardCVCChange,
    validatePaymentInfo,
    confirmCardPayment,
  } = useStripeValidations();

  const {
    fields,
    setField,
    validateField,
    validateForm,
    resetForm,
  } = useFormControls(billingContactForm);

  if (status !== 'PENDING') { // Redirect to details screen if we're in the edit flow
    navigate(`${eventListingsURL}/${token}/details`, { replace: true });
    return null; // Prevent rendering of the rest of the component
  }

  if (initialLoad) return null;

  const setBillingContactInfoToSubmitterContactInfo = () => {
    setField({
      target: {
        name: 'billingContactName',
        value: eventObj.submitterName,
      },
    });
    setField({
      target: {
        name: 'billingContactEmail',
        value: eventObj.submitterEmail,
      },
    });
  };

  const setBillingContactInfoReadOnlyOnChange = (e) => {
    const useExistingSelected = e.target.value === 'true';
    setBillingContactInfoReadOnly(useExistingSelected);
    if (useExistingSelected) {
      setBillingContactInfoToSubmitterContactInfo();
    } else resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowNonFieldError(false);
    if (validatePaymentInfo()) {
      if (validateForm()) {
        try {
          const confirmCardPaymentResponse = await confirmCardPayment(
            paymentIntentClientSecret,
            fields.billingContactName.value,
            fields.billingContactEmail.value,
          );
          setIsSubmitting(false);

          if (confirmCardPaymentResponse.error) {
            throw new Error('Error submitting card payment');
          }
          // Send GTM event
          pushEvent('paymentScreenSubmit', {
            event_listing_cost: window.eventListings.eventListingPriceInPennies / 100,
          });

          navigate(`${eventListingsURL}/${token}/confirmation`);
          scrollToTopOrError();
        } catch (err) {
          console.error(err);
          setShowNonFieldError(true);
        } finally {
          setIsSubmitting(false);
        }
      }
    }
    setIsSubmitting(false);
  };

  const formattedPublishDate = formatDate(eventObj.publishDate, true);
  const publishDateString = formattedPublishDate
    ? (
      <>
        Scheduled to publish on{' '}
        <span className={styles.scheduledDateString}>{formattedPublishDate}</span>
      </>
    )
    : 'Publish immediately';

  return (
    <>
      {isSubmitting && <LoadingScreen />}
      <PageHeading
        title="Finalize Order and Checkout"
        description="Provide your payment details to complete the purchase."
      />
      <div className="row">
        <div className={`columns small-12 large-5 large-push-7 ${styles['small-screen-spacer']}`}>
          <PriceSummaryBox
            purchasedItem="Single event promotion"
            price={eventListingPriceInDollars}
            details={publishDateString}
          />
        </div>
        <div className="columns small-12 large-7 large-pull-5">
          <SubSection
            title="Payment information"
            variant="stretch"
            extraHeaderClass="header--closer"
            description={(
              <PaymentInfoCTA
                cta={(
                  <>
                    Email us at
                    {' '}
                    <a href={`mailto:${eventListingContactEmail}`} target="_blank" rel="noreferrer">{eventListingContactEmail}</a>
                    {' '}
                    to create an invoice.
                  </>
                )}
              />
            )}
          >
            <PaymentInfo
              displayCTA
              validateCardNumber={handleCardNumberChange}
              validateCardExpiry={handleCardExpiryChange}
              validateCardCVC={handleCardCVCChange}
              cardFields={cardFields}
              amountOwed={eventListingPriceInDollars * 100}
            />
          </SubSection>
          <div className={styles['contact-info-wrapper']}>
            <SubSection title="Billing contact information">
              <EventListingsBillingContactInfo
                billingContactEmail={fields.billingContactEmail}
                billingContactName={fields.billingContactName}
                billingContactInfoReadOnly={billingContactInfoReadOnly}
                setBillingContactInfoReadOnlyOnChange={setBillingContactInfoReadOnlyOnChange}
                setField={setField}
                validateField={validateField}
              />
            </SubSection>
          </div>
          <MessageBanner isClosable={false} messageType="warning">
            By posting to Industry Dive you agree to the terms of the{' '}
            <a
              className={`${styles['policy-link']} analytics t-event-listings-policy`}
              href={window.eventListings.eventListingContentPolicyUrl}
              target="_blank"
              rel="noreferrer"
            >
              Event Promotion Policy
            </a>.
          </MessageBanner>
          {showNonFieldError && (
            <MessageBanner messageType="error" onClose={() => setShowNonFieldError(false)}>
              <p>
                An error has occurred. Please refresh and try again.{' '}
                If the problem persists, please reach out to&nbsp;
                <a href={`mailto:${eventListingContactEmail}`}>{eventListingContactEmail}</a>.
              </p>
            </MessageBanner>
          )}
          <div className={styles.buttonWrapper}>
            <button type="button" className="button button--medium" onClick={handleSubmit}>Submit</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentScreen;
